import React, { useMemo } from 'react';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import TextInput from 'components/TextInput/TextInput';
import { CircularProgress } from '@material-ui/core';
import {
  getAssignmentDisplayName,
  hasDistinctAlternativeName,
} from 'scenes/ExpenseTracking/utils/assignmentDisplayName';

const useStyles = makeStyles((theme) => ({
  originalName: {
    opacity: 0.7,
    marginLeft: theme.spacing(0.5),
  },
}));

/**
 * Label of a littera option: `number. name - projectKey`, `name - projectKey`
 * or `name`, where `name` is the Kustannusseuranta display name (the
 * alternative name when one is set). Works for both the mapped options and
 * the raw selected item.
 * @param {object} [option]
 * @returns {string}
 */
export const getComboBoxOptionLabel = (option) => {
  if (!option) {
    return '';
  }
  const base = getAssignmentDisplayName(option);
  const withProject = option.projectKey
    ? `${base} - ${option.projectKey}`
    : base;
  return option.number ? `${option.number}. ${withProject}` : withProject || '';
};

// Search matches the shown label and the original name, so a littera with an
// alternative name is still found by the name everyone else sees.
const filterOptions = createFilterOptions({
  stringify: (option) =>
    `${getComboBoxOptionLabel(option)} ${option.name || ''}`,
});

const ComboBox = (props) => {
  const {
    items,
    selected,
    onChange,
    label,
    index,
    emptyOption,
    emptyAsNull,
    disabled = false,
    loading = false,
    emptyOptionNullText = false,
    error = false,
  } = props;
  const classes = useStyles();
  let options = emptyOption
    ? [
        {
          key: 'Ei määritetty',
          name: 'Ei määritetty',
          value: emptyAsNull ? null : 'none',
        },
      ]
    : [];
  if (items) {
    options = options.concat(
      items.map((item) => ({
        key: item.id,
        name: item.name,
        alternativeName: item.alternativeName,
        projectKey: item.projectKey,
        number: item.number,
        value: item.id,
      }))
    );
  }

  const value = useMemo(() => {
    if (items && selected) {
      return items.filter((item) => item.id === selected)[0];
    } else {
      if (emptyOptionNullText) {
        return null;
      } else {
        return {
          key: 'Ei määritetty',
          name: 'Ei määritetty',
          value: emptyAsNull ? null : 'none',
        };
      }
    }
  }, [selected, items, emptyOptionNullText, emptyAsNull, options]);

  return (
    <Autocomplete
      options={options}
      autoHighlight
      getOptionLabel={getComboBoxOptionLabel}
      filterOptions={filterOptions}
      renderOption={(option) => (
        <span>
          {getComboBoxOptionLabel(option)}
          {hasDistinctAlternativeName(option) ? (
            <span className={classes.originalName}>({option.name})</span>
          ) : null}
        </span>
      )}
      value={value}
      disableClearable
      openOnFocus
      blurOnSelect
      loading={loading}
      onChange={(event, newValue) => onChange(newValue.value, index)}
      disabled={disabled}
      renderInput={(params) => (
        <TextInput
          {...params}
          label={label}
          fullWidth
          //displayEmpty
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            autoComplete: 'new-password',
            error: error,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

ComboBox.defaultProps = {
  items: [],
  emptyOption: true,
};

export default ComboBox;
